import { z } from 'zod';
import { languages, type DunningLevel, type Language } from '../enums.js';

/**
 * Textes du courriel de relance, par langue et par niveau. Même contrat que le courriel de
 * facture : le courriel part dans la langue du dossier, une langue sans rédaction reprend le
 * texte du produit, un champ nul vaut « texte par défaut ». Du texte brut, jamais du HTML.
 *
 * Les marqueurs sont ceux du gabarit du produit : `{number}` et `{level}`. Un seul vocabulaire.
 * La lettre PDF est jointe par l'envoi, elle n'est pas une variable.
 */

export const dunningEmailVariables = ['number', 'level'] as const;
export type DunningEmailVariable = (typeof dunningEmailVariables)[number];

/** Les marqueurs tels qu'ils s'écrivent dans un texte. */
export const dunningTemplatePlaceholders = ['{number}', '{level}'] as const;

export const DUNNING_EMAIL_SUBJECT_MAX_LENGTH = 200;
export const DUNNING_EMAIL_BODY_MAX_LENGTH = 4000;
export const DUNNING_EMAIL_BODY_MAX_LINES = 40;

const MARKER = /\{([^{}\s]{1,40})\}/gu;
const KNOWN: ReadonlySet<string> = new Set(dunningEmailVariables);

/** Marqueurs inconnus d'un texte, sans doublon, dans l'ordre d'apparition. */
export function unknownDunningEmailVariables(text: string): string[] {
  const unknown: string[] = [];
  for (const match of text.matchAll(MARKER)) {
    const name = match[1] ?? '';
    if (!KNOWN.has(name) && !unknown.includes(name)) unknown.push(name);
  }
  return unknown;
}

/** Retours unifiés, bords rognés. Sert à comparer une saisie au texte par défaut. */
export function normalizeDunningEmailText(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
}

function printable(value: string): boolean {
  return Array.from(value).every((char) => {
    const code = char.charCodeAt(0);
    return char === '\n' || (code >= 0x20 && code !== 0x7f);
  });
}

interface FieldRules {
  readonly max: number;
  readonly maxLines: number;
}

function textField(rules: FieldRules, checkVariables: boolean) {
  const bounded = z
    .string()
    .max(rules.max)
    .refine((value) => value.split('\n').length <= rules.maxLines, { message: 'Trop de lignes' })
    .refine(printable, { message: 'Caractère de contrôle interdit' });
  const checked = checkVariables
    ? bounded.superRefine((value, ctx) => {
        const unknown = unknownDunningEmailVariables(value);
        if (unknown.length > 0) {
          ctx.addIssue({
            code: 'custom',
            message: `Variable inconnue : ${unknown.map((name) => `{${name}}`).join(', ')}`,
          });
        }
      })
    : bounded;
  return z
    .string()
    .transform(normalizeDunningEmailText)
    .pipe(checked)
    .transform((value) => (value === '' ? null : value))
    .nullable();
}

function textsSchema(checkVariables: boolean) {
  return z
    .object({
      subject: textField({ max: DUNNING_EMAIL_SUBJECT_MAX_LENGTH, maxLines: 1 }, checkVariables),
      body: textField(
        { max: DUNNING_EMAIL_BODY_MAX_LENGTH, maxLines: DUNNING_EMAIL_BODY_MAX_LINES },
        checkVariables,
      ),
    })
    .strict();
}

/** Textes d'une langue à l'enregistrement : une variable inconnue est refusée. */
export const dunningEmailTextsSchema = textsSchema(true);
export type DunningEmailTexts = z.infer<typeof dunningEmailTextsSchema>;

/** Textes pour l'aperçu : mêmes bornes, une variable inconnue reste visible telle quelle. */
export const dunningEmailDraftTextsSchema = textsSchema(false);

const dunningLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

/**
 * Un niveau à l'enregistrement. Une langue absente, ou dont les deux champs sont nuls, reprend
 * le texte du produit dans cette langue.
 */
export const dunningLevelTemplateSchema = z
  .object({
    level: dunningLevelSchema,
    fr: dunningEmailTextsSchema.optional(),
    de: dunningEmailTextsSchema.optional(),
    en: dunningEmailTextsSchema.optional(),
  } satisfies Record<Language | 'level', z.ZodType>)
  .strict();

/** `PUT /api/dunning/templates` : la liste reçue est celle du cabinet. Un niveau absent = produit. */
export const dunningTemplatesReplaceSchema = z
  .object({
    templates: z
      .array(dunningLevelTemplateSchema)
      .max(3)
      .refine(
        (rows) => new Set(rows.map((row) => row.level)).size === rows.length,
        'Un niveau ne peut apparaître qu’une fois',
      ),
  })
  .strict();
export type DunningTemplatesReplaceInput = z.infer<typeof dunningTemplatesReplaceSchema>;

/** Aperçu : les textes non enregistrés d'une langue, pour un niveau. */
export const dunningEmailPreviewSchema = z
  .object({
    level: dunningLevelSchema,
    language: z.enum(languages),
    texts: dunningEmailDraftTextsSchema,
  })
  .strict();
export type DunningEmailPreviewInput = z.infer<typeof dunningEmailPreviewSchema>;

/** Ce qui est rangé : une langue omise vaut le texte du produit. */
export type DunningStoredTexts = Partial<Record<Language, DunningEmailTexts>>;

export interface DunningEmailDefaultTexts {
  subject: string;
  body: string;
}

export interface DunningEmailTemplateDto {
  level: DunningLevel;
  /** Ce que le cabinet a écrit ; `null` = texte par défaut. Les trois langues, toujours. */
  template: Record<Language, DunningEmailTexts>;
  defaults: Record<Language, DunningEmailDefaultTexts>;
  updatedAt: string | null;
}

/** Rendu du vrai courriel, avec une facture d'exemple. Aucune donnée de patient. */
export interface DunningEmailPreviewDto {
  subject: string;
  html: string;
  text: string;
}
